import { Alert, Button, EmptyState, InfiniteScrollTrigger, Skeleton, Stack } from '@all-manager/design-system-v2';
import NoHistoryIcon from "../../assets/no-historic.svg?react";
import ActivityTimelineFilter from "./ActivityTimelineFilter";
import ActivityTimelineItem from "./ActivityTimelineItem";
import { getTimelineItemKey, useActivityTimeline } from "./useActivityTimeline";

type Props = {
  subTopicId?: string;
  providerId?: string;
  showFilter?: boolean;
};

const SKELETON_ROWS = [0, 1, 2, 3];

export default function ActivityTimeline(props: Props) {
  const {
    items,
    isLoading,
    error,
    currentFilter,
    setFilter,
    counts,
    hasMore,
    isLoadingMore,
    isLoadMoreError,
    handleLoadMore,
  } = useActivityTimeline(props);

  function renderItems() {
    // Each filter is its own server query: only the list area shows the skeleton
    // so the filter chips stay in place while switching.
    if (isLoading) {
      return (
        <Stack spacing={1} aria-busy="true" aria-label="Chargement de l'historique">
          {SKELETON_ROWS.map((row) => (
            <Skeleton key={row} variant="rounded" height={56} />
          ))}
        </Stack>
      );
    }

    if (items.length === 0) {
      return currentFilter === 'all' ? (
        <EmptyState
          title="Aucune activité"
          subtitle="Les rendez-vous, échanges, devis et documents enregistrés apparaîtront ici automatiquement."
          image={<NoHistoryIcon />}
        />
      ) : (
        <EmptyState
          title="Aucune activité de ce type"
          subtitle="Choisissez un autre filtre ou affichez toute l'activité."
          action={(
            <Button variant="outlined" size="small" onClick={() => setFilter('all')}>
              Tout afficher
            </Button>
          )}
        />
      );
    }

    return (
      <Stack>
        {items.map((item, index) => (
          <ActivityTimelineItem
            key={getTimelineItemKey(item)}
            item={item}
            isFirst={index === 0}
            isLast={index === items.length - 1 && !hasMore}
            showDetails
          />
        ))}
        <InfiniteScrollTrigger
          hasMore={hasMore}
          isLoading={isLoadingMore}
          isError={isLoadMoreError}
          onLoadMore={handleLoadMore}
          label="Afficher plus d'activités"
        />
      </Stack>
    );
  }

  if (error) {
    return (
      <Alert severity="error">
        L&apos;historique n&apos;a pas pu être chargé. Rechargez la page pour réessayer.
      </Alert>
    );
  }

  return (
    <Stack spacing={2}>
      {props.showFilter !== false ? (
        <ActivityTimelineFilter
          currentFilter={currentFilter}
          onFilterChange={setFilter}
          counts={counts}
        />
      ) : null}

      {renderItems()}
    </Stack>
  );
}
